// Reads the service breakdown and the proof obligations off an order.
//
// Mirrors functions/order-proof.js and the Flutter ProofRequirement. Kept as a
// separate pure module so it can be unit-tested without React or Firestore.

import { addOnById, flowerById, localized, packageById } from './serviceCatalog';

/**
 * Whether `careType` (an admin-typed free-text value) means "Xüsusi".
 *
 * Deliberately does NOT just call `.toLowerCase()` directly: JavaScript's
 * default Unicode case folding maps the Turkish/Azerbaijani capital dotted
 * "İ" (U+0130) to "i̇" (a plain "i" PLUS a combining dot above, U+0307),
 * not to a bare "i" -- so `'Xüsusİ'.toLowerCase() === 'xüsusi'` is FALSE
 * even though a human reading both would call them identical. Replacing
 * "İ" with a plain "i" before lowercasing avoids that combining-mark
 * mismatch entirely.
 */
function isXususiCareType(careType) {
  return String(careType || '').trim().replace(/İ/g, 'i').toLowerCase() === 'xüsusi';
}

const LEGACY_PREMIUM_HINTS = ['premium', 'премиум'];
const LEGACY_STANDARD_HINTS = ['standard', 'standart', 'стандарт'];
const LEGACY_BASIC_HINTS = ['basic', 'əsas', 'esas', 'базов', 'бэйсик'];

function packageIdentity(data, snapshot) {
  const servicePackage = data.servicePackage;
  const serviceText = typeof servicePackage === 'string' ? servicePackage :
    (servicePackage && typeof servicePackage === 'object'
      ? `${servicePackage.id || ''} ${servicePackage.name || ''} ${servicePackage.packageName || ''}` : '');
  return `${snapshot?.packageId || ''} ${snapshot?.packageName || ''} ${data.packageId || ''} ` +
    `${data.packageName || ''} ${serviceText}`.toLocaleLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * What PakMazar ADMIN proof this order requires.
 *
 * ADMIN PROOF IS NOT THE CUSTOMER'S REPORT ENTITLEMENT. It is the internal
 * evidence stored under `photos.*`, and it is UNIVERSAL:
 *
 *   before photos    : always required, every package;
 *   after photos     : always required, every package;
 *   completion video : always required, every package.
 *
 * Mirrors resolveProofRequirement() in functions/order-proof.js. Approval is
 * decided by this and never by what the customer bought.
 */
export function orderProof(order) {
  const data = order || {};
  const snapshot = data.packageSnapshot;
  const identity = packageIdentity(data, snapshot);
  let photoRequired;
  let videoRequired;
  let source;

  if (snapshot && typeof snapshot === 'object' &&
      (typeof snapshot.requiresPhotoReport === 'boolean' ||
       typeof snapshot.requiresVideoReport === 'boolean')) {
    photoRequired = Boolean(snapshot.requiresPhotoReport);
    videoRequired = Boolean(snapshot.requiresVideoReport);
    source = 'snapshot';
  } else if (typeof data.requiresPhotoReport === 'boolean' ||
             typeof data.requiresVideoReport === 'boolean') {
    photoRequired = Boolean(data.requiresPhotoReport);
    videoRequired = Boolean(data.requiresVideoReport);
    source = 'denormalised';
  } else {
    const catalogEntry = packageById(data.packageId);
    if (catalogEntry) {
      photoRequired = catalogEntry.requiresPhotoReport;
      videoRequired = catalogEntry.requiresVideoReport;
      source = 'catalog';
    } else {
      const key = identity;
      const isPremium = LEGACY_PREMIUM_HINTS.some(h => key.includes(h));
      const isStandard = LEGACY_STANDARD_HINTS.some(h => key.includes(h));
      const isBasic = LEGACY_BASIC_HINTS.some(h => key.includes(h));
      photoRequired = isPremium || isStandard || !isBasic;
      videoRequired = isPremium;
      source = 'legacy';
    }
  }

  // THE ADMIN PROOF RULE, applied after resolution and unconditionally: no
  // stored flag can lower it, so a stale `requiresPhotoReport: false` cannot
  // make an order look approvable with no evidence.
  photoRequired = true;
  videoRequired = true;

  return { photoRequired, videoRequired, source };
}


/** Explicit alias — admin proof, as opposed to the customer entitlement. */
export const orderAdminProof = orderProof;

/**
 * What of the worker's SINGLE `photos.*` upload the CUSTOMER is actually
 * shown on their own order-detail screen. Read-only for admins; never
 * approval evidence -- `orderProof` above is the sole authority for that.
 *
 *   Photos : shown to every customer, unconditionally. Always `true` -- no
 *            stored flag, catalog entry, or anything else can lower it any
 *            more (external review round 2, finding #2: a stored
 *            `customerPhotoReport: false` must not keep hiding photos now
 *            that the confirmed policy is "everyone gets before/after
 *            photos"). The field itself still exists on the data model for
 *            historical/audit purposes; it is simply never read here.
 *
 *   Video  : shown ONLY when this order's packageSnapshot recorded
 *            careType "Xüsusi" AT CREATION TIME. Nothing else may raise or
 *            lower this (external review round 2, finding #1) -- not a
 *            stored `customerVideoReport` flag, not an add-on, not
 *            `visitVideoRequired`, not a legacy catalog/tier-name guess.
 *            An order with no careType at all (predates the data-driven
 *            catalog, or never recorded one) shows NO video to the
 *            customer under this strict rule -- a deliberate, flagged
 *            product decision, not an oversight.
 */
export function orderCustomerReportEntitlement(order) {
  const data = order || {};
  const snapshot = data.packageSnapshot;

  const photoRequired = true;
  const videoRequired = Boolean(snapshot && typeof snapshot === 'object' &&
      typeof snapshot.careType === 'string' &&
      isXususiCareType(snapshot.careType));

  return {
    photoRequired,
    videoRequired,
    source: videoRequired ? 'careType' : 'default',
  };
}

/** The internal admin-proof media as stored on the order. */
export function adminProofMedia(order) {
  const photos = (order && order.photos) || {};
  return {
    before: Array.isArray(photos.beforeUrls) ? photos.beforeUrls : [],
    after: Array.isArray(photos.afterUrls) ? photos.afterUrls : [],
    video: typeof photos.videoUrl === 'string' ? photos.videoUrl : '',
  };
}

/** The customer-facing service report as stored on the order. */
export function clientReportMedia(order) {
  const report = (order && order.clientReport) || {};
  return {
    before: Array.isArray(report.beforeUrls) ? report.beforeUrls : [],
    after: Array.isArray(report.afterUrls) ? report.afterUrls : [],
    video: typeof report.videoUrl === 'string' ? report.videoUrl : '',
    sentAt: report.sentAt || null,
  };
}

/**
 * What the customer actually sees for this order, regardless of which of
 * the two upload generations produced it.
 *
 * An OLD in-flight/completed order that already sent a separate
 * `clientReport.*` (the now-retired two-stage flow) is shown exactly as
 * before -- that historical data is never reinterpreted. A NEW order (no
 * `clientReport` at all) falls back to the worker's single `photos.*`
 * upload, filtered by [orderCustomerReportEntitlement] exactly the way the
 * customer's own mobile order-detail screen does -- so this admin panel
 * never shows "no customer media" for an order that actually has some,
 * just filed under the new single-upload model.
 */
export function customerVisibleMedia(order) {
  const legacy = clientReportMedia(order);
  if (legacy.before.length > 0 || legacy.after.length > 0 || legacy.video) {
    return legacy;
  }

  const entitlement = orderCustomerReportEntitlement(order);
  const proof = adminProofMedia(order);
  return {
    before: proof.before,
    after: proof.after,
    video: entitlement.videoRequired ? proof.video : '',
    sentAt: null,
  };
}

/**
 * True only for a URL a browser can actually load.
 *
 * An older worker build wrote Android device paths (`/data/user/0/...`,
 * `/storage/emulated/0/...`) straight into Firestore. Those render as broken
 * thumbnails, so the panel says so instead of pretending the media uploaded.
 */
export function isDisplayableMediaUrl(value) {
  if (typeof value !== 'string') return false;
  const text = value.trim();
  return /^https?:\/\//i.test(text);
}

/** Any stored media reference that is not a usable URL. */
export function undisplayableMedia(urls) {
  return (Array.isArray(urls) ? urls : [])
    .filter(u => !isDisplayableMediaUrl(u));
}

/** Whether the uploaded ADMIN proof satisfies the order's obligations. */
export function orderProofSatisfied(order) {
  const proof = orderProof(order);
  const photos = (order && order.photos) || {};
  const before = Array.isArray(photos.beforeUrls) ? photos.beforeUrls : [];
  const after = Array.isArray(photos.afterUrls) ? photos.afterUrls : [];
  const video = typeof photos.videoUrl === 'string' ? photos.videoUrl : '';
  if (proof.photoRequired && (before.length < 2 || after.length < 2)) return false;
  if (proof.videoRequired && video.length === 0) return false;
  return true;
}

const lineName = (stored, catalogEntry, lang) => {
  if (stored && stored.localizedName && stored.localizedName[lang]) {
    return stored.localizedName[lang];
  }
  if (catalogEntry) return localized(catalogEntry, lang);
  return (stored && stored.name) || (stored && stored.id) || '';
};

/**
 * The display breakdown for an order: package, included services, flowers and
 * add-ons — each with the price that applied when the order was placed.
 */
export function orderServiceLines(order, lang = 'en') {
  const data = order || {};
  const snapshot = data.packageSnapshot || {};
  const catalogEntry = packageById(snapshot.packageId || data.packageId);

  const includedServices =
    (snapshot.localizedIncludedServices && snapshot.localizedIncludedServices[lang]) ||
    snapshot.includedServices ||
    (catalogEntry ? catalogEntry.includes[lang] || catalogEntry.includes.en : []) ||
    [];

  const flowerId = data.flowerId || '';
  const flowerCatalog = flowerById(flowerId);
  const flower = flowerId && flowerId !== 'no_flowers'
    ? {
        name: flowerCatalog
          ? localized(flowerCatalog, lang)
          : (data.flowerName || flowerId),
        price: Number(data.flowerPrice || 0),
        quoted: data.flowerPriceType === 'from' ||
          data.flowerQuoteStatus === 'pending_quote',
      }
    : null;

  const addOns = (Array.isArray(data.addOns) ? data.addOns : []).map(line => {
    const catalogAddOn = addOnById(line && line.id);
    return {
      id: (line && line.id) || '',
      name: lineName(line, catalogAddOn, lang),
      price: Number((line && line.price) || 0),
      quoted: (line && line.priceType === 'from') ||
        (line && line.quoteStatus === 'pending_quote'),
    };
  });

  const isAnnual = snapshot.packageType === 'annual' ||
    (catalogEntry && catalogEntry.packageType === 'annual');

  return {
    packageName:
      (snapshot.localizedPackageName && snapshot.localizedPackageName[lang]) ||
      snapshot.packageName ||
      (catalogEntry ? localized(catalogEntry, lang) : '') ||
      data.packageName ||
      '—',
    packagePrice: Number(snapshot.packagePrice || data.totalAmount || 0),
    // "Green area" (Yaşıllıq sahəsi) -- a 4th service_packages key segment
    // alongside size/careType, added 2026-11. `null` (not `false`) means
    // this order predates the feature, or was placed against a city with
    // no green-aware entries yet -- never rendered as a confirmed "No" (see
    // OrdersPage.js, which shows "—" for null, same convention as every
    // other "missing on an old order" field in this file).
    greenArea: typeof snapshot.greenArea === 'boolean' ? snapshot.greenArea : null,
    includedServices,
    flower,
    addOns,
    isAnnual,
    visitLabel: data.visitNumber
      ? `visit ${data.visitNumber}${snapshot.totalVisits ? ` of ${snapshot.totalVisits}` : ''}`
      : '',
    hasPendingQuote:
      (flower && flower.quoted) || addOns.some(a => a.quoted) ||
      data.flowerQuoteStatus === 'pending_quote',
    // EXPRESS CLEANING (product rule, approved 2026-09): a fixed, never
    // promo-discounted surcharge for a slot booked less than 48h (but at
    // least 24h) before its start. Server-verified in
    // functions/order-pricing.js -- read here only for display.
    isExpress: Boolean(data.isExpress),
    expressFee: Number(data.expressFee || 0),
  };
}

/**
 * What an admin should be told about a `paymentNeedsReview` order, or null
 * if the order doesn't need review at all.
 *
 * EXPRESS CLEANING (Codex review, accepted mitigation 2026-09): a Payriff
 * checkout link is only priced/validated at CREATION time. Nothing
 * re-checks the 24h/48h schedule classification at payment CONFIRMATION, so
 * the backend (functions/index.js's flagExpressScheduleDriftIfNeeded)
 * flags -- but never blocks -- an order whose classification drifted
 * between link creation and actual payment, by setting
 * `paymentNeedsReview` + `paymentVerificationSource: 'express-schedule-drift'`.
 *
 * The SAME `paymentNeedsReview` field is also set by other, unrelated code
 * paths (a captured-amount/purchase-facts mismatch at create-precheck/
 * client-verify/payriff-callback time, or applyPaymentState's own
 * needsManualReview() check on an ambiguous Payriff status) -- this must
 * never assume Express is the only reason a review is needed, so any
 * source not specifically recognised still surfaces a generic warning
 * naming the raw source, rather than silently showing nothing.
 *
 * Admin-only: callers must never render this to a customer or worker --
 * neither of those apps reads or displays this field at all.
 *
 * @param {object} order
 * @return {{title: string, body: string}|null}
 */
export function paymentReviewExplanation(order) {
  if (!order || !order.paymentNeedsReview) return null;
  const source = order.paymentVerificationSource || '';
  if (source === 'express-schedule-drift') {
    return {
      title: 'Payment needs review — Express schedule drift',
      body: 'The payment was valid and captured, but the order’s Express ' +
        '(24–48h) scheduling classification changed after the payment ' +
        'link was created — the slot may now be Express, no longer ' +
        'Express, or less than 24h away. No money is wrong; confirm the ' +
        'scheduled slot and pricing are still correct for this order.',
    };
  }
  if (['create-precheck-mismatch', 'client-verify-mismatch', 'payriff-callback-mismatch'].includes(source)) {
    return {
      title: 'Payment needs review — details mismatch',
      body: 'The captured Payriff payment did not match what this order ' +
        'expected (amount, currency, or order reference). Verify the ' +
        'payment in the Payriff dashboard before trusting it.',
    };
  }
  return {
    title: 'Payment needs review',
    body: source
      ? `This order was flagged for manual payment review (source: ${source}).`
      : 'This order was flagged for manual payment review.',
  };
}

/** Human labels for whatever proof this order is still missing. */
export function missingProofLabels(order) {
  const proof = orderProof(order);
  const photos = (order && order.photos) || {};
  const before = Array.isArray(photos.beforeUrls) ? photos.beforeUrls : [];
  const after = Array.isArray(photos.afterUrls) ? photos.afterUrls : [];
  const video = typeof photos.videoUrl === 'string' ? photos.videoUrl : '';
  const missing = [];
  if (proof.photoRequired) {
    if (before.length < 2) missing.push('Before photos');
    if (after.length < 2) missing.push('After photos');
  }
  if (proof.videoRequired && video.length === 0) missing.push('Completion video');
  return missing;
}

/**
 * Turns a Functions error into something an admin can act on.
 *
 * The callable throws typed HttpsErrors; a bare "internal" almost always means
 * the call never reached the function at all (not deployed, or deployed to a
 * different region than the client is calling).
 */
export function reviewErrorMessage(error) {
  const code = String((error && error.code) || '').replace(/^functions\//, '');
  const detail = (error && error.message) || '';

  // Admin-side callables attach a machine-readable `reason`, so a refusal is
  // never a bare code an admin has to guess at.
  const details = (error && error.details) || {};
  switch (String(details.reason || '')) {
    case 'no_gateway_payment':
      return 'This order has no Payriff payment, so there is nothing to refund.';
    case 'provider_unreachable':
      return 'Payriff could not be reached to verify the payment. Nothing was ' +
        'refunded — try again in a moment.';
    case 'not_refundable':
      return `Payriff reports this payment as ${details.providerStatus || 'not approved'}, ` +
        'so it cannot be refunded.';
    case 'captured_amount_unavailable':
      return 'Payriff did not return a verified captured amount for this ' +
        'payment, so nothing was refunded. Try again in a moment or check ' +
        'the payment directly in the Payriff dashboard.';
    case 'invalid_refund_amount':
      return `That refund amount is not valid: ${detail}`;
    case 'already_stopped':
      return 'This service has already been stopped.';
    case 'already_final':
      return 'This order is already finished and cannot be stopped.';
    case 'not_stoppable_from_status':
      return `An order in state "${details.status || 'unknown'}" cannot be stopped.`;
    case 'reason_required':
      return 'A reason of at least 5 characters is required.';
    case 'already_decided':
      return 'This complaint has already been decided.';
    case 'note_required':
      return 'A decision note is required — the customer is shown it.';
    case 'refund_amount_required':
      return 'Enter the refund amount before approving.';
    case 'complaint_already_open':
      return 'This order already has an open complaint.';
    default:
      break;
  }

  switch (code) {
    case 'failed-precondition':
      return `Required proof is missing, or this order is no longer awaiting review. (${detail})`;
    case 'permission-denied':
      return 'You do not have permission to approve this work.';
    case 'not-found':
      return 'The order could not be found.';
    case 'invalid-argument':
      return `That request was not valid: ${detail}`;
    case 'unauthenticated':
      return 'Your admin session has expired. Sign in again and retry.';
    case 'internal':
      return 'Could not reach the review service. The reviewWorkSubmission ' +
        'function may not be deployed to europe-west1 yet — deploy Functions ' +
        'and retry.';
    default:
      return detail || `Unexpected error (${code || 'unknown'}).`;
  }
}
